/**
 * Why an SMS failed — two code lists, one locale namespace (`smsFailReason`):
 *
 *  - {@link SMS_FAIL_REASON_KEYS}: `sent_sms.failReason` (status `FAILED`,
 *    `ActionResult.smsFailReason`; in dryrun the refusal the provider WOULD
 *    have given, beside `smsOutcome: "dryrun"`).
 *  - {@link SMS_RUN_FAILURE_KEYS}: the codes that fail the whole action
 *    (`ActionResult` FAILURE, `retryable: false`) before any message exists.
 *    The consumer writes them as the action's `errorMessage` / `smsFailReason`.
 *
 * MIRROR of main-consumer `src/lib/sent-sms-repo.ts` `SmsFailReason` and
 * `SmsRunFailure` (the only writer) and of the comment on main-backend
 * `org/nikita/app/sms_sent/SentSms.java` `failReason`; drift-checked by
 * e2e_main `scripts/check-sms-reasons-drift.sh`. An unknown code renders as the
 * generic `unknown` line carrying the raw code.
 */
export const SMS_FAIL_REASON_KEYS = {
  /** No answer in time (or a server error): the message may or may not have gone out — never resent. */
  "sms-timeout": "timeout",
  /** The provider throttled three times in a row. */
  "sms-throttled": "throttled",
  /** The platform's monthly SMS spend limit was reached. */
  "sms-quota": "quota",
  /** The platform's SMS account is suspended or under review. */
  "sms-account": "account",
  /** The provider refused this message (number, content or configuration). */
  "sms-rejected": "rejected",
  /** The trigger carried no identity to claim the message by — sent nothing rather than risk twice. */
  "sms-unclaimable": "unclaimable",
} as const;

export const SMS_RUN_FAILURE_KEYS = {
  /** More recipients than one run may text (50). Nothing was sent. */
  "sms-too-many-recipients": "tooManyRecipients",
  /** The account brake is latched (spend limit or suspension); nothing was attempted. */
  sms_account_blocked: "accountBlocked",
} as const;

export type SmsFailReasonCode = keyof typeof SMS_FAIL_REASON_KEYS;
export type SmsRunFailureCode = keyof typeof SMS_RUN_FAILURE_KEYS;
export type SmsFailReasonKey =
  | (typeof SMS_FAIL_REASON_KEYS)[SmsFailReasonCode]
  | (typeof SMS_RUN_FAILURE_KEYS)[SmsRunFailureCode];

export const SMS_FAIL_REASON_CODES = Object.keys(SMS_FAIL_REASON_KEYS) as SmsFailReasonCode[];
export const SMS_RUN_FAILURE_CODES = Object.keys(SMS_RUN_FAILURE_KEYS) as SmsRunFailureCode[];

/** The locale key for a fail OR run-failure code, or null for one this build does not know. */
export function smsFailReasonKey(code: string): SmsFailReasonKey | null {
  if (Object.prototype.hasOwnProperty.call(SMS_FAIL_REASON_KEYS, code)) {
    return SMS_FAIL_REASON_KEYS[code as SmsFailReasonCode];
  }
  if (Object.prototype.hasOwnProperty.call(SMS_RUN_FAILURE_KEYS, code)) {
    return SMS_RUN_FAILURE_KEYS[code as SmsRunFailureCode];
  }
  return null;
}
